import { Card, Descriptions, Divider, Drawer, Space, Tooltip } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { BrushIcon, FilterIcon, SideLeftIcon } from 'components/Icons';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxButton } from 'components/RevxButton';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { SectionHeading } from 'components/RevxTypography';
import dayjs from 'dayjs';
import { SortParam } from 'domains/appDomain';
import { Equipment } from 'domains/eqptDomain';
import { isUndefined } from 'lodash-es';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EqptFilterForm } from './EqptFilterForm';

const PAGE_SIZE = 10;

// the filter form only exposes a submit button, the icons in the panel header
// trigger it the same way the platform filter layout does
const clickButton = (selector: string) => {
  const btn = document.querySelector(selector) as HTMLButtonElement | null;
  btn?.click();
};

const toSortParam = (
  sorter: SorterResult<Equipment> | SorterResult<Equipment>[]
): SortParam | undefined => {
  const current = Array.isArray(sorter) ? sorter[0] : sorter;
  const field = (current?.field ?? current?.columnKey) as string | undefined;
  if (!current?.order || !field) {
    return undefined;
  }
  return {
    field,
    order: current.order === 'ascend' ? 'asc' : 'desc',
  };
};

const EqptBaseSelect = () => {
  const { t } = useTranslation();
  const eqptBaseSelectVisible = useAppSelector(
    (state) => state.eqpt.eqptBaseSelect.visible
  );
  const dispatch = useAppDispatch();

  // table data
  const dataSource = useAppSelector(
    (state) => state?.eqpt?.eqptBaseSelect.data?.content || []
  );
  const total = useAppSelector(
    (state) => state.eqpt.eqptBaseSelect.data?.totalElements || 0
  );
  const currentPage = useAppSelector(
    (state) => state.eqpt.eqptBaseSelect.currentPage
  );
  const filterObj = useAppSelector(
    (state) => state.eqpt.eqptBaseSelect.filterObj
  );
  const sort = useAppSelector((state) => state.eqpt.eqptBaseSelect.sort);
  const loading = useAppSelector(
    (state) => state.loading.effects.eqpt.getListForBaseSelect
  );

  const [curItem, setCurItem] = useState<Equipment>();
  const [filterCollapsed, setFilterCollapsed] = useState(false);

  const onClose = () => {
    setCurItem(undefined);
    setFilterCollapsed(false);
    dispatch.eqpt.resetEqptBaseSelect();
  };

  useEffect(() => {
    if (!eqptBaseSelectVisible) {
      return;
    }
    dispatch.eqpt.getListForBaseSelect({
      page: {
        currentPage: currentPage,
        pageSize: PAGE_SIZE,
      },
      query: { ...filterObj },
      sort,
    });
  }, [eqptBaseSelectVisible, currentPage, filterObj, sort, dispatch.eqpt]);

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          dispatch.eqpt.setEqptBaseSelectCurrentPage(pagination.current);
          break;
        case 'sort':
          // server side sorting: reset to the first page on every sort change
          dispatch.eqpt.setEqptBaseSelectState({
            sort: toSortParam(sorter),
            currentPage: 1,
          });
          break;
      }
    },
    [dispatch.eqpt]
  );

  const sortOrderOf = (field: string) => {
    if (sort?.field !== field) {
      return null;
    }
    return sort.order === 'asc' ? ('ascend' as const) : ('descend' as const);
  };

  const onSave = async () => {
    if (!isUndefined(curItem)) {
      await dispatch.eqpt.createOrgFromBase(curItem.id);
      onClose();
    }
  };

  const columns: TableColumnsType<Equipment> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      fixed: 'left',
      sorter: true,
      sortOrder: sortOrderOf('id'),
    },
    {
      title: t('common.revxId'),
      dataIndex: 'revxId',
      sorter: true,
      sortOrder: sortOrderOf('revxId'),
    },
    {
      title: t('platform.eqpt.table.columns.manufacturer'),
      dataIndex: 'manufacturer',
      sorter: true,
      sortOrder: sortOrderOf('manufacturer'),
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      sorter: true,
      sortOrder: sortOrderOf('name'),
    },
    {
      title: t('platform.eqpt.table.columns.printUnits'),
      dataIndex: 'printUnits',
      sorter: true,
      sortOrder: sortOrderOf('printUnits'),
    },
    {
      title: t('platform.eqpt.table.columns.hourlyIncrement'),
      dataIndex: 'hourlyIncrement',
      valueType: 'money',
      sorter: true,
      sortOrder: sortOrderOf('hourlyIncrement'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      render: (value, record) => (
        <Space size="small">
          <a key="select" onClick={() => setCurItem(record)}>
            {t('common.select')}
          </a>
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      destroyOnClose
      className="eqpt-base-select-drawer"
      title={t('platform.eqpt.form.title.create')}
      placement="right"
      width={'min(1180px, 95vw)'}
      closable={true}
      onClose={onClose}
      visible={eqptBaseSelectVisible}
    >
      <div className="eqpt-base-select-body">
        <Card
          hidden={filterCollapsed}
          className="eqpt-base-select-filter"
          size="small"
          title={
            <RevxFlexLayout justify="space-between">
              <SectionHeading style={{ margin: 0 }}>
                {t('platform.filter.title')}
              </SectionHeading>
              <Space
                size="middle"
                style={{ cursor: 'pointer', color: '#757575', fontSize: 16 }}
              >
                <Tooltip
                  title={t('platform.filter.filterBtnLabel')}
                  trigger={['hover', 'click']}
                >
                  <span onClick={() => clickButton('.base-select-filter-btn')}>
                    <FilterIcon style={{ fontSize: 18 }} />
                  </span>
                </Tooltip>
                <Tooltip
                  title={t('platform.filter.clearFilterBtnLabel')}
                  trigger={['hover', 'click']}
                >
                  <span
                    onClick={() => clickButton('.base-select-clear-filter-btn')}
                  >
                    <BrushIcon style={{ fontSize: 19 }} />
                  </span>
                </Tooltip>
                <Tooltip title={t('platform.filter.hideFilters')}>
                  <span onClick={() => setFilterCollapsed(true)}>
                    <SideLeftIcon style={{ fontSize: 17 }} />
                  </span>
                </Tooltip>
              </Space>
            </RevxFlexLayout>
          }
        >
          <EqptFilterForm scope="baseSelect" />
        </Card>
        {filterCollapsed && (
          <Tooltip title={t('platform.filter.showFilters')}>
            <RevxButton
              type="primary"
              brandLightPurple
              className="eqpt-base-select-show-filter"
              icon={
                <span style={{ color: 'white', verticalAlign: 'middle' }}>
                  <FilterIcon style={{ fontSize: 18 }} />
                </span>
              }
              onClick={() => setFilterCollapsed(false)}
            />
          </Tooltip>
        )}
        <div className="eqpt-base-select-table" data-testid="eqpt-list-column">
          <RevxTable<Equipment>
            size="small"
            loading={loading}
            columns={columns}
            className="drawer-table-sticky"
            dataSource={dataSource}
            pagination={{
              pageSize: PAGE_SIZE,
              current: currentPage,
              total: total,
              showSizeChanger: false,
            }}
            onChange={onTableChange}
          />

          {Boolean(curItem) && (
            <>
              <Divider orientation="left">
                {t('platform.eqpt.baseSelect.selectedDescTitle')}
              </Divider>
              <Descriptions
                bordered
                size="small"
                extra={[
                  <Space key="space">
                    <RevxButton key="save" type="primary" onClick={onSave}>
                      {t('common.save')}
                    </RevxButton>
                    <RevxButton onClick={onClose}>
                      {t('common.cancel')}
                    </RevxButton>
                  </Space>,
                ]}
              >
                <Descriptions.Item label={t('common.revxId')}>
                  {curItem?.revxId}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('platform.eqpt.table.columns.manufacturer')}
                >
                  {curItem?.manufacturer || ''}
                </Descriptions.Item>
                <Descriptions.Item label={t('common.name')}>
                  {curItem?.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('platform.eqpt.table.columns.printUnits')}
                >
                  {curItem?.printUnits}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t('platform.eqpt.table.columns.hourlyIncrement')}
                >
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(curItem?.hourlyIncrement || 0)}
                </Descriptions.Item>
                <Descriptions.Item label={t('common.createdAt')}>
                  {dayjs(curItem?.createAt).format('YYYY-MM-DD')}
                </Descriptions.Item>
              </Descriptions>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default EqptBaseSelect;
